import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API, LOGO_URL_WHITE } from "../lib/utils";
import EntityManager from "./EntityManager";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { LogOut, ExternalLink } from "lucide-react";

const PACKAGE_FIELDS = [
  { key: "name_fr", label: "Nom (FR)", type: "text" },
  { key: "name_en", label: "Nom (EN)", type: "text" },
  { key: "description_fr", label: "Description (FR)", type: "textarea", full: true },
  { key: "description_en", label: "Description (EN)", type: "textarea", full: true },
  { key: "amount", label: "Montant ($ CAD)", type: "number", step: "0.01" },
  { key: "sessions", label: "Nombre de séances", type: "number" },
  { key: "currency", label: "Devise", type: "text", default: "cad" },
  { key: "discipline", label: "Discipline", type: "select", options: [
    { value: "natation", label: "Natation" },
    { value: "triathlon", label: "Triathlon" },
    { value: "supplement", label: "Supplément (non réservable)" },
  ], default: "natation" },
  { key: "featured", label: "Mis en avant", type: "checkbox", hint: "Affiché « Le plus choisi »", default: false },
  { key: "order", label: "Ordre d'affichage", type: "number" },
  { key: "active", label: "Visible sur le site", type: "checkbox", default: true },
];

const PROGRAM_FIELDS = [
  { key: "slug", label: "Identifiant (slug)", type: "text" },
  { key: "category", label: "Catégorie", type: "select", options: [
    { value: "kids", label: "Enfants & jeunes" },
    { value: "adults", label: "Adultes & forme physique" },
  ], default: "kids" },
  { key: "title_fr", label: "Titre (FR)", type: "text" },
  { key: "title_en", label: "Titre (EN)", type: "text" },
  { key: "levels_fr", label: "Niveaux (FR)", type: "text" },
  { key: "levels_en", label: "Niveaux (EN)", type: "text" },
  { key: "age_fr", label: "Âge (FR)", type: "text" },
  { key: "age_en", label: "Âge (EN)", type: "text" },
  { key: "desc_fr", label: "Description (FR)", type: "textarea", full: true },
  { key: "desc_en", label: "Description (EN)", type: "textarea", full: true },
  { key: "order", label: "Ordre d'affichage", type: "number" },
  { key: "active", label: "Visible sur le site", type: "checkbox", default: true },
];

const TESTIMONIAL_FIELDS = [
  { key: "name", label: "Nom", type: "text" },
  { key: "role_fr", label: "Rôle (FR)", type: "text" },
  { key: "role_en", label: "Rôle (EN)", type: "text" },
  { key: "quote_fr", label: "Témoignage (FR)", type: "textarea", full: true },
  { key: "quote_en", label: "Témoignage (EN)", type: "textarea", full: true },
  { key: "rating", label: "Note (1-5)", type: "number" },
  { key: "order", label: "Ordre d'affichage", type: "number" },
  { key: "active", label: "Visible sur le site", type: "checkbox", default: true },
];

const COACH_FIELDS = [
  { key: "name", label: "Nom", type: "text" },
  { key: "role_fr", label: "Rôle (FR)", type: "text" },
  { key: "role_en", label: "Rôle (EN)", type: "text" },
  { key: "bio_fr", label: "Bio (FR)", type: "textarea", full: true },
  { key: "bio_en", label: "Bio (EN)", type: "textarea", full: true },
  { key: "discipline", label: "Discipline", type: "text" },
  { key: "photo", label: "URL de la photo", type: "text", full: true },
  { key: "order", label: "Ordre d'affichage", type: "number" },
  { key: "active", label: "Visible sur le site", type: "checkbox", default: true },
];

const BookingsTable = () => {
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/bookings`).then((r) => setBookings(r.data)).catch(() => {});
  }, []);
  const badge = (s) => {
    const map = { paid: "bg-green-100 text-green-700", confirmed: "bg-green-100 text-green-700", unpaid: "bg-amber-100 text-amber-700", pending: "bg-amber-100 text-amber-700", cancelled: "bg-red-100 text-red-700" };
    return <span className={`px-2 py-0.5 text-xs font-bold ${map[s] || "bg-gray-100 text-gray-600"}`}>{s}</span>;
  };
  return (
    <div data-testid="admin-bookings">
      <h2 className="hd-italic text-3xl text-forest mb-6">Réservations ({bookings.length})</h2>
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm">
          <thead className="bg-bone">
            <tr>
              {["Date séance", "Nom", "Courriel", "Téléphone", "Formule", "Niveau", "Statut", "Paiement"].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-heading uppercase tracking-wide text-xs text-forest">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`admin-booking-${b.id}`} className="border-t border-border hover:bg-bone/40">
                <td className="px-4 py-3">{b.preferred_date}{b.preferred_time ? ` ${b.preferred_time}` : ""}</td>
                <td className="px-4 py-3">{b.full_name}</td>
                <td className="px-4 py-3">{b.email}</td>
                <td className="px-4 py-3">{b.phone}</td>
                <td className="px-4 py-3">{b.package_id}</td>
                <td className="px-4 py-3">{b.level}</td>
                <td className="px-4 py-3">{badge(b.status)}</td>
                <td className="px-4 py-3">{badge(b.payment_status)}</td>
              </tr>
            ))}
            {bookings.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Aucune réservation.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DAY_FR = { mon: "Lun", tue: "Mar", wed: "Mer", thu: "Jeu", fri: "Ven", sat: "Sam", sun: "Dim" };
const PER_FR = { am: "Matin", pm: "Après-midi", eve: "Soir" };
const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const formatAvailability = (a) => {
  if (!a || Object.keys(a).length === 0) return "—";
  return DAY_ORDER.filter((d) => a[d] && a[d].length)
    .map((d) => `${DAY_FR[d]}: ${a[d].map((p) => PER_FR[p]).join(", ")}`)
    .join(" · ");
};

const ClientsTable = () => {
  const [clients, setClients] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/clients`).then((r) => setClients(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-clients">
      <h2 className="hd-italic text-3xl text-forest mb-6">Clients ({clients.length})</h2>
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm">
          <thead className="bg-bone">
            <tr>
              {["Nom", "Courriel", "Téléphone", "Adresse", "Disponibilités", "Inscrit le"].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-heading uppercase tracking-wide text-xs text-forest">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} data-testid={`admin-client-${c.id}`} className="border-t border-border hover:bg-bone/40 align-top">
                <td className="px-4 py-3 font-semibold">{c.name}</td>
                <td className="px-4 py-3">{c.email}</td>
                <td className="px-4 py-3">{c.phone || "—"}</td>
                <td className="px-4 py-3">{[c.address, c.city, c.postal_code, c.province].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-4 py-3 max-w-xs">{formatAvailability(c.availability)}</td>
                <td className="px-4 py-3 whitespace-nowrap">{c.created_at ? c.created_at.slice(0, 10) : "—"}</td>
              </tr>
            ))}
            {clients.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Aucun client inscrit.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-bone">
      <header className="bg-forest text-white">
        <div className="container-flow flex items-center justify-between py-5">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL_WHITE} alt="FLOW" className="h-9 w-auto object-contain" />
            <div>
              <p className="font-heading uppercase tracking-widest text-[0.65rem] font-bold text-energy">Espace admin</p>
              <h1 className="hd-italic text-xl leading-none">FLOW</h1>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-white/80 hover:text-white inline-flex items-center gap-1.5">
              <ExternalLink size={15} /> Voir le site
            </a>
            <span className="text-sm text-white/60 hidden sm:inline">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm font-bold hover:text-energy">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="container-flow py-10">
        <Tabs defaultValue="packages" className="w-full">
          <TabsList className="mb-8 flex-wrap h-auto bg-white border border-border p-1">
            <TabsTrigger value="packages" data-testid="admin-tab-packages">Tarifs</TabsTrigger>
            <TabsTrigger value="programs" data-testid="admin-tab-programs">Programmes</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="admin-tab-testimonials">Témoignages</TabsTrigger>
            <TabsTrigger value="coaches" data-testid="admin-tab-coaches">Coachs</TabsTrigger>
            <TabsTrigger value="clients" data-testid="admin-tab-clients">Clients</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Réservations</TabsTrigger>
          </TabsList>

          <TabsContent value="packages">
            <EntityManager endpoint="packages" title="Tarifs / Formules" fields={PACKAGE_FIELDS}
              columns={[
                { key: "name_fr", label: "Nom" },
                { key: "amount", label: "Montant", render: (i) => `${i.amount} $` },
                { key: "discipline", label: "Discipline" },
                { key: "order", label: "Ordre" },
                { key: "active", label: "État" },
              ]} />
          </TabsContent>
          <TabsContent value="programs">
            <EntityManager endpoint="programs" title="Programmes de natation" fields={PROGRAM_FIELDS}
              columns={[
                { key: "title_fr", label: "Titre" },
                { key: "category", label: "Catégorie" },
                { key: "levels_fr", label: "Niveaux" },
                { key: "order", label: "Ordre" },
                { key: "active", label: "État" },
              ]} />
          </TabsContent>
          <TabsContent value="testimonials">
            <EntityManager endpoint="testimonials" title="Témoignages" fields={TESTIMONIAL_FIELDS}
              columns={[
                { key: "name", label: "Nom" },
                { key: "quote_fr", label: "Témoignage" },
                { key: "rating", label: "Note" },
                { key: "active", label: "État" },
              ]} />
          </TabsContent>
          <TabsContent value="coaches">
            <EntityManager endpoint="coaches" title="Coachs" fields={COACH_FIELDS}
              columns={[
                { key: "name", label: "Nom" },
                { key: "role_fr", label: "Rôle" },
                { key: "order", label: "Ordre" },
                { key: "active", label: "État" },
              ]} />
          </TabsContent>
          <TabsContent value="clients">
            <ClientsTable />
          </TabsContent>
          <TabsContent value="bookings">
            <BookingsTable />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
